import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge, Button, StatusDot } from '@/components/atoms';
import { WorkflowConfigOverlay } from '@/components/WorkflowConfigOverlay/WorkflowConfigOverlay';
import {
  useRerunExecution,
  useToggleWorkflowActive,
  useTriggerWorkflow,
  useWorkflow,
  useWorkflowExecutions,
  useWorkflowOverrides,
  useWorkflowStats,
} from '@/hooks/useWorkflows';
import { ApiError } from '@/services/api';
import { useStarred } from '@/store/useStarred';
import type { ExecutionStatus } from '@/types/workflow';

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

import styles from './WorkflowDetail.module.css';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = {
  success: 'ok',
  error: 'crit',
  running: 'info',
  waiting: 'warn',
};

type ExecutionFilter = 'all' | ExecutionStatus;

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = s / 60;
  return `${m.toFixed(1)}min`;
}

export function WorkflowDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const navigate = useNavigate();
  const { data: workflow, isLoading } = useWorkflow(id);
  const { data: stats } = useWorkflowStats(id);
  const { data: executions } = useWorkflowExecutions(id, 50);
  const { data: overrides } = useWorkflowOverrides(id);

  const triggerMutation = useTriggerWorkflow();
  const toggleMutation = useToggleWorkflowActive();
  const rerunMutation = useRerunExecution();

  const starred = useStarred((s) => s.ids);
  const toggleStarred = useStarred((s) => s.toggle);
  const isStarred = id ? starred.includes(id) : false;

  const [msg, setMsg] = useState<string | null>(null);
  const [execFilter, setExecFilter] = useState<ExecutionFilter>('all');
  const [rerunningId, setRerunningId] = useState<string | null>(null);

  if (isLoading) return <p className={styles.muted}>A carregar…</p>;
  if (!workflow || !id) return <p>Workflow não encontrado.</p>;

  const filteredExecutions =
    execFilter === 'all'
      ? executions ?? []
      : (executions ?? []).filter((e) => e.status === execFilter);

  // N8N public API can only fire workflows with a Webhook / Form / Chat
  // trigger — Manual + Schedule have no remote-trigger endpoint.
  const canTriggerViaApi =
    workflow.active && workflow.trigger_type === 'webhook';

  async function handleTrigger() {
    setMsg(null);
    try {
      const res = await triggerMutation.mutateAsync(id!);
      setMsg(`Disparado. Execução: ${res.execution_id ?? '(sem ID)'}`);
    } catch (e) {
      setMsg(`Erro: ${describeError(e)}`);
    }
  }

  async function handleToggle() {
    setMsg(null);
    try {
      await toggleMutation.mutateAsync({ id: id!, active: !workflow!.active });
      setMsg(workflow!.active ? 'Pausado.' : 'Activado.');
    } catch (e) {
      setMsg(`Erro: ${describeError(e)}`);
    }
  }

  async function handleRerun(execId: string) {
    setRerunningId(execId);
    setMsg(null);
    try {
      const res = await rerunMutation.mutateAsync(execId);
      setMsg(`Re-executado. Nova execução: ${res.execution_id ?? '(sem ID)'}`);
    } catch (e) {
      setMsg(`Erro re-run: ${describeError(e)}`);
    } finally {
      setRerunningId(null);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to="/workflows" className={styles.back}>
          ← Workflows
        </Link>
        <button
          className={`${styles.starBtn} ${isStarred ? styles.starBtnActive : ''}`}
          onClick={() => toggleStarred(id)}
          aria-label={isStarred ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          aria-pressed={isStarred}
          title={isStarred ? 'Remover favorito' : 'Adicionar favorito'}
        >
          {isStarred ? 'Favoritado' : 'Favoritar'}
        </button>
        <h1 className={styles.title}>{workflow.name}</h1>
        <Badge tone={workflow.active ? 'ok' : 'neutral'} size="sm">
          {workflow.active ? 'activo' : 'pausado'}
        </Badge>
        {workflow.is_override && <Badge tone="warn" size="sm">override</Badge>}
        {stats?.last_run_status && (
          <Badge tone={STATUS_TONE[stats.last_run_status] ?? 'neutral'} size="sm">
            última: {stats.last_run_status}
          </Badge>
        )}
      </header>

      <div className={styles.meta}>
        <span>ID: {workflow.id}</span>
        {workflow.trigger_type && <span>Trigger: {workflow.trigger_type}</span>}
        {workflow.schedule_expression && (
          <span className={styles.metaCode}>cron: {workflow.schedule_expression}</span>
        )}
        {workflow.tags.length > 0 && (
          <span>
            tags: {workflow.tags.join(', ')}
          </span>
        )}
      </div>

      {stats && stats.sample_size > 0 && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statNumber}>
              {(stats.success_rate * 100).toFixed(0)}%
            </span>
            <span className={styles.statLabel}>
              taxa sucesso ({stats.sample_size} amostras)
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statNumber}>{stats.success_count}</span>
            <span className={styles.statLabel}>
              <StatusDot state="ok" /> sucesso
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statNumber}>{stats.error_count}</span>
            <span className={styles.statLabel}>
              <StatusDot state="crit" /> erros
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statNumber}>
              {formatDuration(stats.avg_duration_ms)}
            </span>
            <span className={styles.statLabel}>duração média</span>
          </div>
          {stats.last_run_at && (
            <div className={styles.statCard}>
              <span className={styles.statNumberSmall}>
                {new Date(stats.last_run_at).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className={styles.statLabel}>última corrida</span>
            </div>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <Button
          onClick={handleTrigger}
          loading={triggerMutation.isPending}
          disabled={!canTriggerViaApi}
          title={
            canTriggerViaApi
              ? 'Disparar workflow via webhook'
              : 'Trigger manual/schedule não suporta disparo via API — adicione um Webhook trigger ou rode no editor N8N'
          }
        >
          Disparar agora
        </Button>
        <Button variant="secondary" onClick={handleToggle} loading={toggleMutation.isPending}>
          {workflow.active ? 'Pausar' : 'Activar'}
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/workflows/${id}/override`)}>
          Criar override
        </Button>
        {msg && <span className={styles.msg}>{msg}</span>}
      </div>
      {!canTriggerViaApi && (
        <p className={styles.triggerHint}>
          Este workflow usa trigger <code>{workflow.trigger_type ?? 'desconhecido'}</code>.
          A API pública do N8N só dispara workflows com trigger Webhook / Form / Chat.
          Para disparar via Wiredesk, adicione um Webhook trigger ao workflow.
        </p>
      )}

      <WorkflowConfigOverlay workflowId={id} workflowName={workflow.name} />

      <header className={styles.subHead}>
        <h2 className={styles.sectionTitle}>Execuções recentes</h2>
        <div className={styles.execFilters}>
          {(['all', 'success', 'error', 'running'] as const).map((f) => (
            <button
              key={f}
              className={`${styles.chipFilter} ${execFilter === f ? styles.chipFilterActive : ''}`}
              onClick={() => setExecFilter(f)}
            >
              {f === 'all' ? 'Todas' : f}
            </button>
          ))}
        </div>
      </header>

      {executions && executions.length === 0 && (
        <p className={styles.muted}>Sem execuções nas últimas amostras.</p>
      )}
      {executions && filteredExecutions.length === 0 && executions.length > 0 && (
        <p className={styles.muted}>Sem execuções com filtro {execFilter}.</p>
      )}

      <ul className={styles.execList}>
        {filteredExecutions.map((e) => (
          <li key={e.id} className={styles.execRow}>
            <StatusDot
              state={
                e.status === 'success' ? 'ok' : e.status === 'error' ? 'crit' : 'warn'
              }
            />
            <Link to={`/workflows/${id}/execucoes/${e.id}`} className={styles.execLink}>
              #{e.id}
            </Link>
            <Badge tone={STATUS_TONE[e.status] ?? 'neutral'} size="xs">
              {e.status}
            </Badge>
            <span className={styles.execMeta}>
              {new Date(e.started_at).toLocaleString('pt-BR')}
            </span>
            {e.finished_at && e.started_at && (
              <span className={styles.execMeta}>
                {formatDuration(
                  new Date(e.finished_at).getTime() - new Date(e.started_at).getTime(),
                )}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRerun(e.id)}
              loading={rerunningId === e.id}
              disabled={rerunMutation.isPending}
            >
              Re-run
            </Button>
          </li>
        ))}
      </ul>

      {overrides && overrides.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Overrides existentes</h2>
          <ul className={styles.execList}>
            {overrides.map((o) => (
              <li key={o.id} className={styles.execRow}>
                <Badge tone="warn" size="xs">
                  {o.status}
                </Badge>
                <Link
                  to={`/workflows/${o.override_workflow_id}`}
                  className={styles.execLink}
                >
                  {o.name}
                </Link>
                <span className={styles.execMeta}>
                  {new Date(o.created_at).toLocaleDateString('pt-BR')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
